import { useCallback, useEffect, useMemo, useReducer } from 'react';
import axios from 'axios';
import { ppaUrls } from 'config/urls';
import {
    getPpaScenarioCountFromResponse,
    getPpaScenariosFromResponse,
} from 'utils/ppaHelpers';
import type { ScenarioRow } from '../types';

type ScenarioListRequestPayload = {
    project_id: number;
    my_scenarios: boolean;
    user_id?: string;
};

type ScenariosState = {
    fetchedScenariosByProject: Record<number, ScenarioRow[]>;
    scenarioCountByProject: Record<number, number>;
    loadingScenarioProjectIds: Record<number, boolean>;
};

type ScenariosAction =
    | { type: 'SET_LOADING'; projectId: number; loading: boolean }
    | { type: 'SET_SCENARIOS'; projectId: number; scenarios: ScenarioRow[]; count: number }
    | { type: 'REMOVE_PROJECT'; projectId: number }
    | { type: 'REMOVE_SCENARIO'; projectId: number; scenarioId: number }
    | { type: 'RESET' };

const initialState: ScenariosState = {
    fetchedScenariosByProject: {},
    scenarioCountByProject: {},
    loadingScenarioProjectIds: {},
};

const scenariosReducer = (state: ScenariosState, action: ScenariosAction): ScenariosState => {
    switch (action.type) {
        case 'SET_LOADING':
            return {
                ...state,
                loadingScenarioProjectIds: {
                    ...state.loadingScenarioProjectIds,
                    [action.projectId]: action.loading,
                },
            };
        case 'SET_SCENARIOS':
            return {
                ...state,
                fetchedScenariosByProject: {
                    ...state.fetchedScenariosByProject,
                    [action.projectId]: action.scenarios,
                },
                scenarioCountByProject: {
                    ...state.scenarioCountByProject,
                    [action.projectId]: action.count,
                },
            };
        case 'REMOVE_PROJECT': {
            const { [action.projectId]: _scenarios, ...fetchedScenariosByProject } =
                state.fetchedScenariosByProject;
            const { [action.projectId]: _count, ...scenarioCountByProject } = state.scenarioCountByProject;
            const { [action.projectId]: _loading, ...loadingScenarioProjectIds } =
                state.loadingScenarioProjectIds;
            return { fetchedScenariosByProject, scenarioCountByProject, loadingScenarioProjectIds };
        }
        case 'REMOVE_SCENARIO': {
            const existingScenarios = state.fetchedScenariosByProject[action.projectId];
            if (!existingScenarios) {
                return state;
            }
            const nextScenarios = existingScenarios.filter(
                (scenario) => String(scenario.scenario_id) !== String(action.scenarioId),
            );
            if (nextScenarios.length === existingScenarios.length) {
                return state;
            }
            const existingCount = state.scenarioCountByProject[action.projectId];
            return {
                ...state,
                fetchedScenariosByProject: {
                    ...state.fetchedScenariosByProject,
                    [action.projectId]: nextScenarios,
                },
                scenarioCountByProject:
                    typeof existingCount === 'number'
                        ? {
                              ...state.scenarioCountByProject,
                              [action.projectId]: Math.max(existingCount - 1, 0),
                          }
                        : state.scenarioCountByProject,
            };
        }
        case 'RESET':
            return initialState;
        default:
            return state;
    }
};

const buildScenarioListPayload = (
    projectId: number,
    myScenarios: boolean,
    userId?: string,
): ScenarioListRequestPayload => ({
    project_id: projectId,
    my_scenarios: myScenarios,
    ...(userId ? { user_id: userId } : {}),
});

/** Total scenarios in the project (unfiltered). Used for child_limit / disable logic when "My Scenarios" is on. */
const fetchTotalScenarioCountForProject = async (
    projectId: number,
    userId?: string,
): Promise<number> => {
    const response = await axios.post(
        ppaUrls.getScenarioListUrl,
        buildScenarioListPayload(projectId, false, userId),
    );
    const allScenarios = getPpaScenariosFromResponse<ScenarioRow>(response.data);
    return getPpaScenarioCountFromResponse(response.data, allScenarios.length);
};

const loadScenariosForProject = async (
    projectId: number,
    { myScenarioOnly, userId }: { myScenarioOnly: boolean; userId?: string },
) => {
    const response = await axios.post(
        ppaUrls.getScenarioListUrl,
        buildScenarioListPayload(projectId, myScenarioOnly, userId),
    );
    const scenarios = getPpaScenariosFromResponse<ScenarioRow>(response.data);
    const scenarioCount = myScenarioOnly
        ? await fetchTotalScenarioCountForProject(projectId, userId)
        : getPpaScenarioCountFromResponse(response.data, scenarios.length);
    return { scenarios, scenarioCount };
};

type UseProjectScenariosParams = {
    scenariosByProjectProp?: Record<number, ScenarioRow[]>;
    myScenarioOnly: boolean;
    userId?: string;
};

export const useProjectScenarios = ({
    scenariosByProjectProp,
    myScenarioOnly,
    userId,
}: UseProjectScenariosParams) => {
    const [state, dispatch] = useReducer(scenariosReducer, initialState);

    useEffect(() => {
        dispatch({ type: 'RESET' });
    }, [myScenarioOnly]);

    const scenariosByProjectMerged = useMemo(() => {
        if (!scenariosByProjectProp) {
            return state.fetchedScenariosByProject;
        }
        return { ...state.fetchedScenariosByProject, ...scenariosByProjectProp };
    }, [state.fetchedScenariosByProject, scenariosByProjectProp]);

    const fetchScenariosByProject = useCallback(
        async (projectId: number) => {
            const parentOwnsProjectScenarios =
                scenariosByProjectProp &&
                Object.prototype.hasOwnProperty.call(scenariosByProjectProp, projectId);
            if (
                parentOwnsProjectScenarios ||
                state.fetchedScenariosByProject[projectId] !== undefined ||
                state.loadingScenarioProjectIds[projectId]
            ) {
                return;
            }

            dispatch({ type: 'SET_LOADING', projectId, loading: true });
            try {
                const { scenarios, scenarioCount } = await loadScenariosForProject(projectId, {
                    myScenarioOnly,
                    userId,
                });
                dispatch({ type: 'SET_SCENARIOS', projectId, scenarios, count: scenarioCount });
            } finally {
                dispatch({ type: 'SET_LOADING', projectId, loading: false });
            }
        },
        [
            myScenarioOnly,
            scenariosByProjectProp,
            state.fetchedScenariosByProject,
            state.loadingScenarioProjectIds,
            userId,
        ],
    );

    const refreshScenariosForProject = useCallback(async (projectId: number) => {
        dispatch({ type: 'SET_LOADING', projectId, loading: true });
        try {
            const { scenarios, scenarioCount } = await loadScenariosForProject(projectId, {
                myScenarioOnly,
                userId,
            });
            dispatch({ type: 'SET_SCENARIOS', projectId, scenarios, count: scenarioCount });
        } finally {
            dispatch({ type: 'SET_LOADING', projectId, loading: false });
        }
    }, [myScenarioOnly, userId]);

    const removeProjectFromScenarioState = useCallback((projectId: number) => {
        dispatch({ type: 'REMOVE_PROJECT', projectId });
    }, []);

    const removeScenarioFromProjectState = useCallback((projectId: number, scenarioId: number) => {
        dispatch({ type: 'REMOVE_SCENARIO', projectId, scenarioId });
    }, []);

    return {
        scenariosByProjectMerged,
        scenarioCountByProject: state.scenarioCountByProject,
        loadingScenarioProjectIds: state.loadingScenarioProjectIds,
        fetchScenariosByProject,
        refreshScenariosForProject,
        removeProjectFromScenarioState,
        removeScenarioFromProjectState,
    };
};
